// Shared utilities for dropdown and tokenized select controllers

export const TOKEN_COLORS = {
  blue: "bg-blue-100 text-blue-800 border-blue-200",
  green: "bg-green-100 text-green-800 border-green-200",
  purple: "bg-purple-100 text-purple-800 border-purple-200",
  orange: "bg-orange-100 text-orange-800 border-orange-200",
  gray: "bg-gray-100 text-gray-700 border-gray-200"
}

export function getColorClasses(color) {
  return TOKEN_COLORS[color] || TOKEN_COLORS.blue
}

export function createToken(id, name, colorClasses, removeAction, tokenClass = null) {
  const token = document.createElement("div")
  const baseClass = tokenClass || "inline-flex items-center gap-1.5 rounded-full font-medium text-sm px-2.5 py-0.5 border"
  token.className = `${baseClass} ${colorClasses}`
  token.dataset.tokenId = id
  token.id = `tokenized-select-token-${id}`

  const nameSpan = document.createElement("span")
  nameSpan.textContent = name

  const removeButton = document.createElement("button")
  removeButton.type = "button"
  removeButton.className = "hover:opacity-70 transition-opacity"
  removeButton.setAttribute("aria-label", `Remove ${name}`)
  removeButton.dataset.action = removeAction

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
  svg.setAttribute("class", "w-3.5 h-3.5")
  svg.setAttribute("fill", "none")
  svg.setAttribute("stroke", "currentColor")
  svg.setAttribute("viewBox", "0 0 24 24")
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
  path.setAttribute("stroke-linecap", "round")
  path.setAttribute("stroke-linejoin", "round")
  path.setAttribute("stroke-width", "2")
  path.setAttribute("d", "M6 18L18 6M6 6l12 12")
  svg.appendChild(path)
  removeButton.appendChild(svg)

  token.appendChild(nameSpan)
  token.appendChild(removeButton)

  return token
}

// positionDropdown — runtime-measurement exception to no-inline-styles:
// top/bottom values are computed from live DOM measurements and cannot be
// expressed as pre-defined CSS classes.
export function positionDropdown(dropdown, container) {
  const containerRect = container.getBoundingClientRect()
  // Always called after dropdown is visible, so offsetHeight is the real height.
  const dropdownHeight = dropdown.offsetHeight

  const spaceBelow = window.innerHeight - containerRect.bottom
  const spaceAbove = containerRect.top

  if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
    dropdown.style.bottom = `${container.offsetHeight + 4}px`
    dropdown.style.top = "auto"
  } else {
    dropdown.style.top = `${container.offsetHeight + 4}px`
    dropdown.style.bottom = "auto"
  }
}

// Prefers below the trigger; opens above when the panel does not fit in the viewport.
// Uses position:fixed + viewport coordinates so the menu is not clipped by overflow
// ancestors (e.g. rounded table cards). Runtime-measurement exception to no-inline-styles:
// top/left/width/bottom depend on getBoundingClientRect at open time.
export function positionFixedDropdown(dropdown, trigger) {
  const triggerRect = trigger.getBoundingClientRect()
  const dropdownHeight = dropdown.offsetHeight
  const spaceBelow = window.innerHeight - triggerRect.bottom
  const spaceAbove = triggerRect.top

  dropdown.style.left = `${triggerRect.left}px`
  dropdown.style.width = `${triggerRect.width}px`
  dropdown.style.right = "auto"

  if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
    dropdown.style.top = "auto"
    dropdown.style.bottom = `${window.innerHeight - triggerRect.top + 4}px`
  } else {
    dropdown.style.top = `${triggerRect.bottom + 4}px`
    dropdown.style.bottom = "auto"
  }
}


function elementClipsOverflow(el) {
  if (!el || el === document.documentElement) return false
  const s = window.getComputedStyle(el)
  const clipped = new Set(["auto", "hidden", "scroll", "clip"])
  return clipped.has(s.overflow) || clipped.has(s.overflowX) || clipped.has(s.overflowY)
}

// Nearest ancestor that establishes a scroll/clipping box (e.g. modal with overflow-y-auto).
// Popovers are position:absolute inside that subtree, so viewport-only clamping misses the real edge.
function nearestClippingRect(anchor) {
  let el = anchor.parentElement
  while (el && el !== document.documentElement) {
    if (elementClipsOverflow(el)) {
      return el.getBoundingClientRect()
    }
    el = el.parentElement
  }
  return {
    left: 0,
    top: 0,
    right: window.innerWidth,
    bottom: window.innerHeight
  }
}

// Prefers below the anchor; opens above only when the panel height does not fit below the
// anchor within the clamp rect (viewport or nearest overflow ancestor). Horizontally, shifts
// the panel so it stays inside that rect — same runtime-measurement exception as positionDropdown.
export function positionAnchoredPopup(panel, anchor) {
  const anchorRect = anchor.getBoundingClientRect()
  const panelRect = panel.getBoundingClientRect()
  const panelHeight = panelRect.height || panel.offsetHeight || panel.scrollHeight
  const panelWidth = panelRect.width || panel.offsetWidth || 320
  const bounds = nearestClippingRect(anchor)
  const spaceBelow = bounds.bottom - anchorRect.bottom
  const fitsBelow = spaceBelow >= panelHeight
  // Asymmetric insets so clamped panels do not sit flush on the container edge.
  const insetStart = 16
  const insetEnd = 28

  if (fitsBelow) {
    panel.style.bottom = "auto"
    panel.style.top = `${anchor.offsetHeight + 4}px`
  } else {
    panel.style.top = "auto"
    panel.style.bottom = `${anchor.offsetHeight + 4}px`
  }

  // Default: panel is absolutely positioned with Tailwind left-0 (flush to anchor’s left).
  let desiredLeftViewport = anchorRect.left
  if (desiredLeftViewport + panelWidth > bounds.right - insetEnd) {
    desiredLeftViewport = bounds.right - panelWidth - insetEnd
  }
  if (desiredLeftViewport < bounds.left + insetStart) {
    desiredLeftViewport = bounds.left + insetStart
  }

  const leftOffsetPx = desiredLeftViewport - anchorRect.left
  panel.style.left = `${leftOffsetPx}px`
  panel.style.right = "auto"
}

export function toggleClearButton(clearBtn, tokenContainer) {
  if (!clearBtn) return
  const hasTokens = tokenContainer.querySelector("[data-token-id]")
  if (hasTokens) {
    clearBtn.classList.remove("hidden")
  } else {
    clearBtn.classList.add("hidden")
  }
}

export function resetScrollHint(dropdown) {
  const hint = dropdown.querySelector('[data-scroll-hint-target="hint"]')
  if (hint) hint.classList.remove("hidden")
}
